import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from './components/Shell';
import { ErrorBlock, Spinner, Toaster } from './components/ui';
import { queryClient } from './lib/queryClient';
import { useSession } from './lib/session';
import './i18n';
import { EnrollPage, LoginPage, PasswordPage } from './pages/AuthPages';
import { AdminsPage } from './pages/AdminsPage';
import { AuditPage } from './pages/AuditPage';
import { ConfigPage } from './pages/ConfigPage';
import { DashboardPage } from './pages/DashboardPage';
import { MarketEditPage } from './pages/MarketEditPage';
import { MarketsPage } from './pages/MarketsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ProposalsPage } from './pages/ProposalsPage';
import { ReconciliationPage } from './pages/ReconciliationPage';
import { TopUpsPage } from './pages/TopUpsPage';
import { UserDetailPage } from './pages/UserDetailPage';
import { UsersPage } from './pages/UsersPage';
import { WithdrawalsPage } from './pages/WithdrawalsPage';

function Gate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const session = useSession();
  if (session.isLoading) return <Spinner />;
  if (session.isError)
    return <ErrorBlock message={t('errorTitle')} onRetry={() => void session.refetch()} />;
  if (!session.data) return <Navigate to="/login" replace />;
  if (session.data.stage === 'enroll') return <Navigate to="/enroll" replace />;
  if (session.data.mustChangePassword) return <Navigate to="/password" replace />;
  return children;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/enroll" element={<EnrollPage />} />
        <Route path="/password" element={<PasswordPage />} />
        <Route
          element={
            <Gate>
              <Shell />
            </Gate>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="topups" element={<TopUpsPage />} />
          <Route path="withdrawals" element={<WithdrawalsPage />} />
          <Route path="markets" element={<MarketsPage />} />
          <Route path="markets/new" element={<MarketEditPage />} />
          <Route path="markets/:id" element={<MarketEditPage />} />
          <Route path="proposals" element={<ProposalsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="admins" element={<AdminsPage />} />
          <Route path="config" element={<ConfigPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="reconciliation" element={<ReconciliationPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      <Toaster />
    </QueryClientProvider>
  );
}
